import { Pause, Play, X, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

export const WALK_STEPS = [
    {
        tab: "proposed",
        targets: ["change-package-card"],
        text: "An authorised steward publishes a machine-readable change package.",
    },
    {
        tab: "impact",
        targets: ["impact-node-orig", "impact-node-inter", "impact-node-recv", "impact-node-settle"],
        text: "Each participant assesses the same change within its own custody boundary.",
    },
    {
        tab: "impact",
        scenario: "legacy",
        targets: ["impact-node-settle", "scenario-note-legacy"],
        text: "A red readiness result changes the clarification, transition or activation plan.",
    },
    {
        tab: "activation",
        targets: ["steward-response-card", "readiness-card"],
        text: "The revised package is tested locally and activated through compatible role decisions.",
    },
    {
        tab: "runtime",
        targets: ["feedback-panel"],
        text: "Runtime exceptions and outcomes return bounded evidence to the appropriate steward.",
    },
];

export const clearWalkHighlights = () =>
    document.querySelectorAll(".walk-highlight").forEach((el) => el.classList.remove("walk-highlight"));

export const applyWalkHighlights = (targets) => {
    clearWalkHighlights();
    targets.forEach((t, i) => {
        const el = document.querySelector(`[data-testid='${t}']`);
        if (el) {
            el.classList.add("walk-highlight");
            if (i === 0) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    });
};

export const WalkthroughOverlay = ({
    step,
    paused,
    completed,
    onPrev,
    onNext,
    onPauseToggle,
    onExit,
    onReplay,
}) => (
    <div
        className="fixed bottom-5 right-5 z-40 w-[340px] max-w-[calc(100vw-2.5rem)] rounded-sm border border-slate-200 bg-white p-4 shadow-lg"
        data-testid="walkthrough-overlay"
    >
        <style>{`.walk-highlight{outline:2px solid #172554;outline-offset:4px;border-radius:2px;}`}</style>
        <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">
                {completed ? "Walkthrough complete" : `Guided walkthrough · Step ${step + 1} of ${WALK_STEPS.length}`}
            </p>
            <button
                onClick={onExit}
                data-testid="walkthrough-exit"
                aria-label="Exit walkthrough"
                className="rounded-sm p-1 text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-700"
            >
                <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
        </div>

        <p className="mt-2 text-sm leading-relaxed text-slate-800" data-testid="walkthrough-step-text">
            {completed
                ? "You can now explore every act, scenario and receipt manually."
                : WALK_STEPS[step].text}
        </p>

        {!completed && (
            <div className="mt-3 flex gap-1">
                {WALK_STEPS.map((_, i) => (
                    <span
                        key={i}
                        className={`h-1 flex-1 rounded-sm ${i <= step ? "bg-blue-950" : "bg-slate-200"}`}
                    />
                ))}
            </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {!completed && (
                <>
                    <CtrlBtn onClick={onPrev} disabled={step === 0} testId="walkthrough-prev" label="Previous">
                        <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Previous
                    </CtrlBtn>
                    <CtrlBtn onClick={onNext} testId="walkthrough-next" label="Next">
                        Next
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </CtrlBtn>
                    <CtrlBtn onClick={onPauseToggle} testId="walkthrough-pause" label={paused ? "Resume" : "Pause"}>
                        {paused ? (
                            <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
                        ) : (
                            <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />
                        )}
                        {paused ? "Resume" : "Pause"}
                    </CtrlBtn>
                </>
            )}
            <CtrlBtn onClick={onReplay} testId="walkthrough-replay" label="Replay">
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                Replay
            </CtrlBtn>
        </div>
    </div>
);

function CtrlBtn({ onClick, disabled, testId, children }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
        >
            {children}
        </button>
    );
}
